import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

import { DESIRED_COLOR_OPTIONS } from '../../constants/style-settings';
import { getColorFromName } from '../../utils/getColorFromName';
import { StyleSettings } from './style-settings';

interface DesiredColorSettingsProps {
  colors?: DESIRED_COLOR_OPTIONS[];
  onUpdate: (key: keyof StyleSettings, val: DESIRED_COLOR_OPTIONS) => void;
}

const ColorItem = ({
  active,
  color,
  label,
  onClickItem,
}: {
  active: boolean;
  color: DESIRED_COLOR_OPTIONS;
  label: string;
  onClickItem: (key: keyof StyleSettings, val: DESIRED_COLOR_OPTIONS) => void;
}) => {
  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1',
        active ? 'bg-[#07B1BE1F]' : 'bg-[#0000001F]'
      )}
      onClick={() => onClickItem('desiredColor', color)}
    >
      <div
        className="h-[13px] w-[13px] rounded-full"
        style={{ backgroundColor: getColorFromName(color) }}
      />
      <span className="text-[10px] font-medium leading-none">{label}</span>
      {!active ? <X className="h-[10px] w-[10px]" /> : null}
    </div>
  );
};

export const DesiredColorSettings = ({ colors, onUpdate }: DesiredColorSettingsProps) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);

  const desiredColorOptions = [
    DESIRED_COLOR_OPTIONS.WHITE,
    DESIRED_COLOR_OPTIONS.BLACK,
    DESIRED_COLOR_OPTIONS.BLUE,
    DESIRED_COLOR_OPTIONS.RED,
    DESIRED_COLOR_OPTIONS.GREEN,
    DESIRED_COLOR_OPTIONS.YELLOW,
    DESIRED_COLOR_OPTIONS.GRAY,
    DESIRED_COLOR_OPTIONS.BROWN,
    DESIRED_COLOR_OPTIONS.ORANGE,
    DESIRED_COLOR_OPTIONS.PURPLE,
    DESIRED_COLOR_OPTIONS.PINK,
    DESIRED_COLOR_OPTIONS.BEIGE,
    DESIRED_COLOR_OPTIONS.GOLD,
    DESIRED_COLOR_OPTIONS.SILVER,
    DESIRED_COLOR_OPTIONS.BRONZE,
  ];
  const colorsSet = new Set(colors);
  const activeColors = new Set(desiredColorOptions.filter((color) => colorsSet.has(color)));

  return (
    <Accordion
      type="single"
      className="w-full overflow-hidden rounded-sm bg-[#EFF4F5]"
      onValueChange={(val: string) => setIsOpened(!!val)}
      collapsible
    >
      <AccordionItem value="desired-color-settings">
        <AccordionTrigger className="flex min-h-[40px] w-full cursor-pointer items-center rounded-b-sm px-4 py-2 sm:min-h-[26px] sm:px-2 sm:py-1 lg:min-h-[40px] lg:px-4 lg:py-2">
          <span className="flex-1 text-left text-xs font-semibold lg:text-sm">
            {t('desiredColor')}
          </span>
        </AccordionTrigger>
        {!isOpened && !!activeColors?.size ? (
          <div className="flex flex-wrap gap-1 px-4 pb-3">
            {[...activeColors].map((color) => (
              <ColorItem
                key={color}
                active={activeColors.has(color)}
                color={color}
                label={t(color)}
                onClickItem={onUpdate}
              />
            ))}
          </div>
        ) : null}
        <AccordionContent className="px-4 pb-3">
          <div className="flex flex-wrap gap-1">
            {desiredColorOptions.map((color) => (
              <ColorItem
                key={color}
                active={activeColors.has(color)}
                color={color}
                label={t(color)}
                onClickItem={onUpdate}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
